import { klona } from 'klona';
import type { DatepickerValue } from '@/types';

// Monday 1, Sunday 7
export const getISOWeekDays = () => {
  return [ ...Array(7).keys() ].map(index => index + 1);
}

export const isDateInRange = (
  date: Date,
  rangeStart: Date,
  rangeEnd: Date
): boolean => {
  return date >= rangeStart && date <= rangeEnd;
};

export const addDays = (days: number, date: Date): Date => {
  const mutatedDate = klona(date);
  mutatedDate.setDate(mutatedDate.getDate() + days);
  return mutatedDate;
};

export const isSameDay = (dateOne: Date, dateTwo: Date): boolean => {
  const dayOne = klona(dateOne);
  const dayTwo = klona(dateTwo);
  dayOne.setHours(0, 0, 0, 0);
  dayTwo.setHours(0, 0, 0, 0);
  return dayOne.getTime() === dayTwo.getTime();
};

export const isSameMonth = (dateOne: Date, dateTwo: Date): boolean => {
  return (
    dateOne.getMonth() === dateTwo.getMonth() &&
    dateOne.getFullYear() == dateTwo.getFullYear()
  );
};

// normalize Date to compare Days
export const getNormalizedDate = (date: Date): number => {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const getDifferenceInDays = (dateOne: Date, dateTwo: Date): number => {
  // This method ignores summer-winter-time hours and calculates the exact Day-Difference
  const date1 = Date.UTC(
    dateOne.getUTCFullYear(),
    dateOne.getUTCMonth(),
    dateOne.getUTCDate()
  );
  const date2 = Date.UTC(
    dateTwo.getUTCFullYear(),
    dateTwo.getUTCMonth(),
    dateTwo.getUTCDate()
  );
  const ms = Math.abs(date1 - date2);
  return Math.floor(ms / 1000 / 60 / 60 / 24); // Milliseconds to Days
};

export const getDaysBetweenMonths = (
  monthOne: Date,
  monthTwo: Date
): number => {
  const month1 = klona(monthOne);
  month1.setDate(1);
  const month2 = klona(monthTwo);
  month2.setDate(1);
  return getDifferenceInDays(month1, month2);
};

// Month index 1 = January.
// Example: StartIndex = 1, EndIndex = 16, CurrentYear = 2024 - calculates MonthDays between 1. January 2024 and 1. April 2025
export const getDaysBetweenMonthsByIndex = (
  startIndex: number,
  endIndex: number,
  startYear: number
) => {
  let monthDays = 0;
  for (let i = startIndex; i < endIndex; i++) {
    const yearDifference = Math.floor(i / 12);
    // index and dateIndex are different. Example: StartIndex 1 = January, JS Date Index 1 = February.
    // new Date(year, 1, 0).getDate() is getting the MonthDays of January, because it's getting the Day before the first February.
    const currentMonthDays = new Date(
      startYear + yearDifference,
      i,
      0
    ).getDate();
    monthDays += currentMonthDays;
  }

  return monthDays;
};

export const getMonthsBetweenTwoDates = (
  dateOne: Date,
  dateTwo: Date
): number => {
  let months;
  months = (dateTwo.getUTCFullYear() - dateOne.getUTCFullYear()) * 12;
  months -= dateOne.getUTCMonth();
  months += dateTwo.getUTCMonth();
  return months <= 0 ? 0 : months;
};

export const getISOStringByDatepickerValue = (
  datepickerValue: DatepickerValue
): string => {
  const date = datepickerValue.date as Date;
  if (typeof datepickerValue.time?.h !== 'undefined') {
    date.setHours(datepickerValue.time?.h, datepickerValue.time?.m ?? 0, 0, 0);
  }
  return date.toISOString();
};

export const addMinutes = (date: Date, minutes: number): string => {
  return new Date(date.getTime() + minutes * 60000).toISOString();
};

export const getCalendarWeek = (date: Date): number => {
  const currentDate = date.getTime();
  const yearStart = new Date(date.getFullYear(), 0, 1).getTime();
  const oneDayInMs = 86400000;
  const dayOfYear = (currentDate - yearStart + 1) / oneDayInMs;
  const daysInWeek = 7;
  const week = Math.ceil(dayOfYear / daysInWeek);
  return week;
};
